import { validateSteuernummer } from 'api/v1/users/validateSteuernummber';
import { getUserFinanzamt } from 'api/v2/users/getUserFinanzamt';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useQuery } from 'react-query';
import { UserCacheKeys } from 'types/cacheKeys.types';
import { AccountSettingsFieldsNamesEnum } from 'types/settings.types';
import useDebounce from './useDebounce';
// Validates the Steuernummer (Tax ID) for German accounts based on the German State
// and the actual Tax ID
const useValidateSteuernummer = ({
  name,
  stateValue,
  taxOfficeStateValue,
  steuernummerValue,
  skipValidation = false,
}: {
  name?: string;
  stateValue: string;
  taxOfficeStateValue?: string;
  steuernummerValue: string;
  skipValidation?: boolean;
}): { isValidatingSteuernummer: boolean; finanzamtOffice: string } => {
  const { setError, setValue, clearErrors } = useFormContext();
  const fieldName = name || AccountSettingsFieldsNamesEnum.steuernummer;
  const _steuernummerValue = useDebounce(1000, steuernummerValue);
  // Use taxOfficeState if provided, otherwise fall back to address state
  const effectiveState = taxOfficeStateValue || stateValue;
  const steuernummerQuery = useQuery(
    [
      UserCacheKeys.ValidateSteuernummer,
      { stateValue: effectiveState, _steuernummerValue, fieldName },
    ],
    () => {
      clearErrors(name);
      return validateSteuernummer(effectiveState, _steuernummerValue);
    },
    {
      cacheTime: 0,
      enabled:
        !skipValidation &&
        !!(effectiveState && _steuernummerValue?.length >= 10),
      onSuccess: (data) => {
        // Prevent race condition: don't update state if validation was skipped after query started
        if (skipValidation) return;

        if (!data?.valid) {
          setValue('steuernummerValid', false);
          return setError(fieldName, {
            type: 'custom',
            message: 'settings.steuernummer.any.invalidCombination',
          });
        }
        setValue('steuernummerValid', true);
      },
      onError: () => {
        // Prevent race condition: don't update state if validation was skipped after query started
        if (skipValidation) return;

        setValue('steuernummerValid', false);
        setError(fieldName, {
          type: 'custom',
          message: 'settings.steuernummer.any.invalidCombination',
        });
      },
    },
  );

  useEffect(() => {
    if (skipValidation) {
      setValue('steuernummerValid', true);
      clearErrors(fieldName);
      return;
    }
    if (steuernummerValue?.length < 10) {
      setValue('steuernummerValid', false);
      setError(fieldName, { type: 'custom', message: '' });
    }
  }, [steuernummerValue, skipValidation]);
  const finanzamtQuery = useQuery(
    [
      UserCacheKeys.UserFinanzamt,
      { _steuernummerValue, stateValue: effectiveState },
    ],
    () => getUserFinanzamt(_steuernummerValue, effectiveState),
    {
      enabled:
        !skipValidation &&
        !!(
          _steuernummerValue &&
          effectiveState &&
          steuernummerQuery.data?.valid
        ),
      cacheTime: 0,
    },
  );

  return {
    isValidatingSteuernummer:
      steuernummerQuery.isLoading || finanzamtQuery.isLoading,
    finanzamtOffice: finanzamtQuery.data?.taxOffice as string,
  };
};

export default useValidateSteuernummer;
